import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiHome, FiArrowLeft, FiAlertCircle } from 'react-icons/fi';
import Button from '../components/Button';

const NotFoundContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  padding: ${props => props.theme.spacing.xl};
  margin-top: 0;
`;

const Content = styled.div`
  text-align: center;
  max-width: 600px;
  width: 100%;
`;

const ErrorIcon = styled.div`
  font-size: 120px;
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.xl};
  animation: bounce 1s ease-in-out infinite;

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-20px); }
  }
`;

const ErrorCode = styled.h1`
  font-size: ${props => props.theme.fontSizes['6xl']};
  font-weight: ${props => props.theme.fontWeights.extrabold};
  color: ${props => props.theme.colors.dark};
  margin-bottom: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.gradient.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${props => props.theme.fontSizes['4xl']};
  }
`;

const ErrorTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes['3xl']};
  color: ${props => props.theme.colors.dark};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-weight: ${props => props.theme.fontWeights.bold};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${props => props.theme.fontSizes['2xl']};
  }
`;

const ErrorMessage = styled.p`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.gray[600]};
  margin-bottom: ${props => props.theme.spacing['3xl']};
  line-height: 1.6;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${props => props.theme.fontSizes.lg};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SuggestionBox = styled.div`
  margin-top: ${props => props.theme.spacing['3xl']};
  padding: ${props => props.theme.spacing.xl};
  background: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows.md};

  h3 {
    font-size: ${props => props.theme.fontSizes.lg};
    color: ${props => props.theme.colors.dark};
    margin-bottom: ${props => props.theme.spacing.md};
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.sm};
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    text-align: left;

    li {
      margin-bottom: ${props => props.theme.spacing.sm};

      a {
        color: ${props => props.theme.colors.primary};
        text-decoration: none;
        transition: ${props => props.theme.transitions.default};
        display: flex;
        align-items: center;
        gap: ${props => props.theme.spacing.xs};

        &:hover {
          color: ${props => props.theme.colors.secondary};
          transform: translateX(5px);
        }
      }
    }
  }
`;

const NotFound = () => {
  return (
    <NotFoundContainer>
      <Content>
        <ErrorIcon>
          <FiAlertCircle />
        </ErrorIcon>

        <ErrorCode>404</ErrorCode>
        
        <ErrorTitle>Page Not Found</ErrorTitle>
        
        <ErrorMessage>
          Oops! The page you're looking for doesn't exist. It might have been moved or deleted.
        </ErrorMessage>

        <ButtonGroup>
          <Button
            as={Link}
            to="/"
            variant="primary"
            size="large"
          >
            <FiHome />
            Go to Homepage
          </Button>
          
          <Button
            variant="secondary"
            size="large"
            onClick={() => window.history.back()}
          >
            <FiArrowLeft />
            Go Back
          </Button>
        </ButtonGroup>

        <SuggestionBox>
          <h3>
            <FiAlertCircle />
            You might be looking for:
          </h3>
          <ul>
            <li>
              <Link to="/courses">→ Browse All Courses</Link>
            </li>
            <li>
              <Link to="/blog">→ Read Our Blog</Link>
            </li>
            <li>
              <Link to="/about">→ About Us</Link>
            </li>
            <li>
              <Link to="/contact">→ Contact Support</Link>
            </li>
          </ul>
        </SuggestionBox>
      </Content>
    </NotFoundContainer>
  );
};

export default NotFound;
